/**
 * @lodex-pro/web-portal/routes/TankImportSection — "Import tanks" on the Tank setup page (tank
 * registry PR 2; no migration). OPERATOR_ADMIN only: the page renders it for the admin, and the
 * API refuses anyone else (MANAGE_OPERATOR_TANKS).
 *
 * Choose a CSV or XLSX, Preview (nothing is written), then "Import N" sends the SAME file again
 * with ?apply=true. The server re-reads the file and the registry on apply, so a row can turn
 * into "Not imported" at write time; the apply's own rows replace the preview's. The file format
 * (header aliases, rules) is the API's: services/operator-tank-import.ts.
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';
import type {
  TankImportResponse,
  TankImportRow,
  TankImportRowStatus,
  TankTagSystem,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { useImportRegistryTanks } from '../hooks/useTankSetupAndRoutes';
import { portalErrorText } from '../components/portal-error-text';
import { BUTTON, CARD, MUTED, PRIMARY_BUTTON, TAG, WARNING } from './operator-tank-labels';

/** The template's header row: one header per column the import reads. */
export const TANK_IMPORT_TEMPLATE_HEADERS = [
  'Tank Name',
  // 13.8 (D-22b): the tank's producer tank ID; the import matches by it first.
  'Producer Tank ID',
  'API Number',
  'Site',
  'Battery',
  'Capacity (bbl)',
  'Height (in)',
  'BBL/in',
  'Tank Type',
  'Catalog Type',
  'SCADA Tag',
  'MQTT Topic',
  'Tags',
] as const;

const TEMPLATE_CSV = `${TANK_IMPORT_TEMPLATE_HEADERS.join(',')}\r\n`;

const STATUS_LABEL: Record<TankImportRowStatus, string> = {
  NEW: 'New',
  UPDATE: 'Update',
  UNCHANGED: 'Unchanged',
  ERROR: 'Not imported',
};

const SYSTEM_LABEL: Record<TankTagSystem, string> = {
  SCADA: 'SCADA',
  MQTT: 'MQTT',
  OTHER: 'Other',
};

const ERROR_TEXT: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '13px',
  margin: '8px 0 0',
};
const CELL: React.CSSProperties = {
  padding: '6px 8px',
  borderTop: '1px solid var(--color-border-primary)',
  verticalAlign: 'top',
  textAlign: 'left',
};

/** Why the server refused the whole file: its reason when it gave one, else in plain words. */
function importErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const reason = err.fields.find((f) => f.path === 'file')?.reason;
    if (reason !== undefined) return reason;
    if (err.code === 'PAYLOAD_TOO_LARGE') return 'That file is too large. The limit is 2 MB.';
  }
  return portalErrorText(
    err,
    'The file could not be read. Use a CSV or XLSX with a tank name column and a well API number, site or battery column.',
  );
}

/** A link to a blank CSV template, built here (a Blob behind an object URL). */
function TemplateLink(): React.JSX.Element | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined;
    const u = URL.createObjectURL(new Blob([TEMPLATE_CSV], { type: 'text/csv' }));
    setUrl(u);
    return (): void => {
      URL.revokeObjectURL(u);
    };
  }, []);
  if (url === null) return null;
  return (
    <a href={url} download="tank-import-template.csv" style={{ color: 'var(--color-link)' }}>
      Download a template
    </a>
  );
}

export function TankImportSection(): React.JSX.Element {
  const importTanks = useImportRegistryTanks();
  const [file, setFile] = React.useState<File | null>(null);
  const [response, setResponse] = React.useState<TankImportResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const busy = importTanks.isPending;

  const run = async (apply: boolean): Promise<void> => {
    if (file === null) return;
    setError(null);
    try {
      setResponse(await importTanks.mutateAsync({ file, apply }));
    } catch (err) {
      setError(importErrorText(err));
    }
  };

  const s = response?.summary;
  const writable = s === undefined ? 0 : s.new + s.update;
  const result = response?.result;

  return (
    <section aria-label="Import tanks" style={{ marginTop: '32px' }}>
      <h2 style={{ fontSize: '17px', marginBottom: '4px' }}>Import tanks</h2>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0 }}>
        A CSV or XLSX file, one tank per row. A row whose producer tank ID matches one of your tanks
        updates it, else a row whose name matches a tank at the same well, site or battery; any
        other row adds a tank. The Tank ID column is the producer&rsquo;s own ID for the tank, not
        its name. Files made before October 2026 that put names there will store those names as
        producer tank IDs. Give exactly one of a well API number, a site or a battery. Blank cells
        keep what is on file. Several tags in one cell are separated by semicolons. The import never
        removes a tag. Nothing is saved until you import. <TemplateLink />
      </p>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          aria-label="Tank file"
          type="file"
          accept=".csv,.xlsx"
          onChange={(e): void => {
            setFile(e.target.files?.[0] ?? null);
            setResponse(null);
            setError(null);
          }}
          style={{ fontSize: '13px' }}
        />
        <button
          type="button"
          style={BUTTON}
          disabled={file === null || busy}
          onClick={(): void => {
            void run(false);
          }}
        >
          Preview
        </button>
      </div>
      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
      {response !== null && s !== undefined && (
        <div style={{ ...CARD, marginTop: '12px' }}>
          {result !== undefined && (
            <p
              role="status"
              style={{ color: 'var(--color-success)', margin: '0 0 8px', fontSize: '13px' }}
            >
              Imported: {result.created} new, {result.updated} updated, {result.tagsAdded} tags
              added.
            </p>
          )}
          <p aria-label="Import summary" style={{ fontSize: '13px', margin: '0 0 8px' }}>
            {s.new} new · {s.update} update · {s.unchanged} unchanged · {s.errors} not imported
          </p>
          <ImportRows rows={response.rows} />
          {!response.applied && (
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                style={PRIMARY_BUTTON}
                disabled={busy || writable === 0}
                onClick={(): void => {
                  void run(true);
                }}
              >
                Import {writable}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ImportRows({ rows }: { readonly rows: readonly TankImportRow[] }): React.JSX.Element {
  if (rows.length === 0) {
    return <p style={{ ...MUTED, fontSize: '13px', margin: 0 }}>The file has no tank rows.</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        aria-label="Import rows"
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}
      >
        <thead>
          <tr style={MUTED}>
            <th style={CELL}>Row</th>
            <th style={CELL}>Tank</th>
            <th style={CELL}>Result</th>
            <th style={CELL}>Where</th>
            <th style={CELL}>Changes</th>
            <th style={CELL}>Tags added</th>
            <th style={CELL}>Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowNumber}>
              <td style={CELL}>{r.rowNumber}</td>
              <td style={CELL}>{r.name === '' ? '—' : r.name}</td>
              <td style={CELL}>
                <span style={r.status === 'ERROR' ? WARNING : TAG}>{STATUS_LABEL[r.status]}</span>
              </td>
              <td style={CELL}>{r.placement ?? '—'}</td>
              <td style={CELL}>{r.changes.length === 0 ? '—' : r.changes.join(', ')}</td>
              <td style={CELL}>
                {r.addTags.length === 0
                  ? '—'
                  : r.addTags.map((t) => `${SYSTEM_LABEL[t.system]}: ${t.tag}`).join(', ')}
              </td>
              <td style={CELL}>{r.detail ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
